'use client';

import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Spinner - Loading indicator component
 *
 * Features:
 * - Three sizes: sm (16px), md (24px), lg (32px)
 * - Color defaults to `text-current` (inherits from parent). Explicit
 *   variants: `accent` (orange), `muted` (gray), `white`.
 * - Smooth rotation animation
 *
 * @example
 * ```tsx
 * <Spinner size="md" />                   // inherits text color from parent
 * <Spinner size="sm" color="accent" />    // orange
 * <Button isLoading>Saving...</Button>    // uses Spinner internally
 * ```
 */
const spinnerVariants = cva('animate-spin text-current', {
  variants: {
    size: {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6',
    },
    color: {
      accent: 'text-accent-500',
      muted: 'text-text-3',
      white: 'text-white',
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

interface SpinnerProps
  extends
    Omit<React.SVGProps<SVGSVGElement>, 'color'>,
    VariantProps<typeof spinnerVariants> {
  /** Screen reader label */
  label?: string;
}

function Spinner({
  className,
  size,
  color,
  label = 'Loading',
  ...props
}: SpinnerProps) {
  return (
    <svg
      role="status"
      aria-label={label}
      viewBox="0 0 24 24"
      className={cn(spinnerVariants({ size, color }), className)}
      {...props}
    >
      <title>{label}</title>
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
        fill="none"
      />
      <path
        className="opacity-90"
        d="M22 12a10 10 0 0 0-10-10"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

export { Spinner };
